import { i18n } from '../i18n.js';
/**
 * audio-settings.js
 * The audio controls UI: one compact trigger that opens an accessible popover
 * with two fully independent channels — Music and Sound Effects — each with a
 * labeled slider (its value IS the actual playback level) and a discoverable
 * per-channel mute. The music engine lives in soundtrack.js and the SFX engine
 * in sound-effects.js; this module only renders/wires their controls.
 */


import { SafeArea } from './safe-area.js';
import { SoundEffects } from './sound-effects.js';
import { Soundtrack } from './soundtrack.js';
import { uiPx } from './ui-scale.js';
let AudioSettings = {};

AudioSettings = (function () {
    const ROOT_ID = 'audio-volume-control';

    let root = null;
    let toggle = null;
    let toggleGlyph = null;
    let panel = null;
    let musicSlider = null;
    let sfxSlider = null;
    let musicMuteBtn = null;
    let sfxMuteBtn = null;
    let musicMuteGlyph = null;
    let sfxMuteGlyph = null;
    let musicPct = null;
    let sfxPct = null;
    let musicRow = null;
    let sfxRow = null;

    let isOpen = false;

    function t(key, fallback, repl) {
        const val = i18n?.t?.(key, repl);
        return (typeof val === 'string' && val && val !== key) ? val : fallback;
    }

    function soundtrack() { return Soundtrack; }
    function soundEffects() { return SoundEffects; }

    function clamp01(v) {
        const n = Number(v);
        return Number.isFinite(n) ? Math.max(0, Math.min(1, n)) : 0;
    }

    function toPct(v) { return Math.round(clamp01(v) * 100); }
    function vmin() { return Math.min(window.innerWidth, window.innerHeight) / 100; }

    /* ONE deliberate column on every layout: Audio to the LEFT of End Turn,
       Return to Lobby directly beneath it, the group sharing the summoner
       column's centre line.

       This used to be gated on `(pointer: coarse)`, leaving desktop on a
       historic below-then-beside search — so the same battle had the volume
       control under End Turn on a laptop and beside it on a tablet, and the
       exit wandered to wherever the search found room. A HUD arrangement is a
       design decision, not something to re-derive per input device; the search
       remains only as the fallback for a layout that genuinely cannot seat the
       declared form. */
    function usesCompactRail() {
        return true;
    }

    /* The short-landscape-phone page boxes, and ONLY those.

       The horizontal three-control rail below was measured to fit on exactly
       these: 198-203px of controls against 207-311px between the board's right
       edge and the safe inset. Desktop and tablet have their own proven
       placement (the exit centred beneath End Turn) and a search that has tests
       covering pin restore, churn immunity and rotation; applying the phone rail
       there would delete all of that on geometry it was never measured against.
       Bounds mirror the phone block in mobile.css. */
    function usesPhoneCommandRail() {
        return window.innerWidth > window.innerHeight
            && window.innerHeight <= 560
            && window.innerWidth <= 1023;
    }


    /* Insets come from the shared SafeArea probe (scripts/ui/safe-area.js);
       fall back to zero so a missing script never breaks popover placement. */
    function safeAreaInsets() {
        return SafeArea?.insets?.() || { top: 0, right: 0, bottom: 0, left: 0 };
    }

    /**
     * The speaker, drawn — not the 🔇🔈🔉🔊 emoji this used to be.
     *
     * Two reasons, and they agree. The repo's icon rule bans emoji as
     * structural icons because they are font-dependent and cannot be themed;
     * and the owner asked for one ink inside this panel — "all elemetns should
     * have same color ... do nto have different colored text" — which an emoji
     * physically cannot honour, since it paints its own colours. Three red-ish
     * mute pucks in a column of pale-lilac rows was the last thing in here
     * still colouring itself.
     *
     * `currentColor` throughout, so the row's ink is the glyph's ink.
     *
     * @param {number} effective 0..1 — silence draws the crossed-out speaker,
     *   and each louder band adds one arc, so the level is still readable at a
     *   glance without a second colour.
     */
    function speakerGlyph(effective) {
        const NS = 'http://www.w3.org/2000/svg';
        const svg = document.createElementNS(NS, 'svg');
        svg.setAttribute('class', 'audio-mute-svg');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');

        const cone = document.createElementNS(NS, 'path');
        cone.setAttribute('fill', 'currentColor');
        cone.setAttribute('d', 'M11 4.8 6.6 8.5H3.6a1 1 0 0 0-1 1v5a1 1 0 0 0 1 1h3l4.4 3.7a.8.8 0 0 0 1.3-.6V5.4a.8.8 0 0 0-1.3-.6Z');
        svg.appendChild(cone);

        if (effective <= 0) {
            const slash = document.createElementNS(NS, 'path');
            slash.setAttribute('stroke', 'currentColor');
            slash.setAttribute('stroke-width', '1.9');
            slash.setAttribute('stroke-linecap', 'round');
            slash.setAttribute('fill', 'none');
            slash.setAttribute('d', 'M15.5 9.8l5 4.4M20.5 9.8l-5 4.4');
            svg.appendChild(slash);
            return svg;
        }

        const arcs = effective < 0.34 ? 1 : (effective < 0.67 ? 2 : 3);
        for (const [index, d] of [
            'M15.3 9.7a3.2 3.2 0 0 1 0 4.6',
            'M17.6 7.7a6.3 6.3 0 0 1 0 8.6',
            'M19.9 5.7a9.4 9.4 0 0 1 0 12.6',
        ].entries()) {
            if (index >= arcs) break;
            const arc = document.createElementNS(NS, 'path');
            arc.setAttribute('stroke', 'currentColor');
            arc.setAttribute('stroke-width', '1.8');
            arc.setAttribute('stroke-linecap', 'round');
            arc.setAttribute('fill', 'none');
            arc.setAttribute('d', d);
            svg.appendChild(arc);
        }
        return svg;
    }

    // --- channel state (raw slider level + mute + effective playback) ---
    // Duck-type each capability (like cards.js does for getEffectiveVolume):
    // embedding surfaces and test harnesses may install partial engines, and a
    // boot-time refresh crash here takes unrelated pipelines down with it.
    function musicRaw() { const s = soundtrack(); return typeof s?.getVolume === 'function' ? clamp01(s.getVolume()) : 0.7; }
    function musicMuted() { const s = soundtrack(); return typeof s?.isMuted === 'function' ? !!s.isMuted() : false; }
    function musicEffective() { return musicMuted() ? 0 : musicRaw(); }
    function sfxRaw() { const s = soundEffects(); return typeof s?.getVolume === 'function' ? clamp01(s.getVolume()) : 1; }
    function sfxMuted() { const s = soundEffects(); return typeof s?.isMuted === 'function' ? !!s.isMuted() : false; }
    function sfxEffective() { return sfxMuted() ? 0 : sfxRaw(); }

    function makeButton(id, className) {
        const btn = document.createElement('button');
        btn.id = id;
        btn.className = className;
        btn.type = 'button';
        return btn;
    }

    function buildChannelRow({ rowClass, muteId, sliderId, labelId, labelKey, labelText }) {
        const row = document.createElement('div');
        row.className = `audio-channel-row ${rowClass}`;

        const muteBtn = makeButton(muteId, 'audio-mute-btn');
        muteBtn.setAttribute('aria-pressed', 'false');
        const muteGlyph = document.createElement('span');
        muteGlyph.className = 'audio-mute-glyph';
        muteGlyph.setAttribute('aria-hidden', 'true');
        muteBtn.appendChild(muteGlyph);

        const main = document.createElement('div');
        main.className = 'audio-channel-main';

        const label = document.createElement('label');
        label.id = labelId;
        label.className = 'audio-channel-label';
        label.setAttribute('for', sliderId);
        label.setAttribute('data-i18n', labelKey);
        label.textContent = labelText;

        const slider = document.createElement('input');
        slider.id = sliderId;
        slider.className = 'audio-slider';
        slider.type = 'range';
        slider.min = '0';
        slider.max = '100';
        slider.step = '1';
        slider.setAttribute('aria-labelledby', labelId);

        main.appendChild(label);
        main.appendChild(slider);

        const pct = document.createElement('span');
        pct.className = 'audio-channel-pct';
        pct.setAttribute('aria-hidden', 'true');

        row.appendChild(muteBtn);
        row.appendChild(main);
        row.appendChild(pct);

        return { row, muteBtn, muteGlyph, slider, pct };
    }

    /**
     * THE MENU SIGIL — three equal bars. The classical menu mark, on purpose.
     *
     * Drawn as SVG rather than typed, for the same reason the full-screen
     * brackets are: `☰` renders as anything from a box to tofu depending on the
     * installed fonts, and a glyph cannot take `currentColor`, a stroke width,
     * or the hover/open transitions the rest of the chrome uses.
     *
     * THREE MARKS WERE REJECTED BEFORE THIS ONE, and the lesson is the same
     * each time: this button is the only chrome on the screen, so it has to be
     * instantly recognisable rather than interesting. A crescent behind bars was
     * invisible at the 34px the rail actually draws. Bars of three DIFFERENT
     * lengths read as a designed mark rather than a menu. A crescent with a star
     * was distinctive and still did not say "menu" — the owner's verdict was
     * "terrible, it needs some intuitive classical symbol for menu".
     *
     * So: equal lengths, equal gaps, round caps. Nothing clever.
     */
    function menuSigil() {
        const NS = 'http://www.w3.org/2000/svg';
        const svg = document.createElementNS(NS, 'svg');
        svg.setAttribute('class', 'audio-settings-glyph chrome-menu-sigil');
        svg.setAttribute('viewBox', '0 0 24 24');
        svg.setAttribute('aria-hidden', 'true');
        svg.setAttribute('focusable', 'false');

        for (const [index, y] of [6.5, 12, 17.5].entries()) {
            const bar = document.createElementNS(NS, 'line');
            bar.setAttribute('class', `chrome-menu-sigil-bar chrome-menu-sigil-bar--${index + 1}`);
            bar.setAttribute('x1', '4.5');
            bar.setAttribute('x2', '19.5');
            bar.setAttribute('y1', String(y));
            bar.setAttribute('y2', String(y));
            bar.setAttribute('stroke', 'currentColor');
            bar.setAttribute('stroke-width', '2.2');
            bar.setAttribute('stroke-linecap', 'round');
            svg.appendChild(bar);
        }
        return svg;
    }

    /* Built in EVERY document, framed or not. A guard used to skip any iframe
       because the Moon Foundry once ran in a same-origin frame that reused
       the parent's control; that frame is gone (the Foundry mounts in-page),
       and the only frame left is a third-party embed — itch.io — whose parent
       has no Moonfall controls at all. Skipping there stripped the audio,
       language and feedback controls from every embedded player. */
    function build() {
        if (document.getElementById(ROOT_ID)) return;
        if (!document.body) return;

        root = document.createElement('div');
        root.id = ROOT_ID;
        root.className = 'audio-volume-control';

        toggle = makeButton('audio-settings-toggle', 'audio-settings-toggle');
        toggle.setAttribute('aria-haspopup', 'true');
        toggle.setAttribute('aria-expanded', 'false');
        toggle.setAttribute('aria-controls', 'audio-settings-panel');
        toggleGlyph = menuSigil();
        toggle.appendChild(toggleGlyph);
        root.appendChild(toggle);

        panel = document.createElement('div');
        panel.id = 'audio-settings-panel';
        panel.className = 'audio-settings-panel';
        panel.setAttribute('role', 'dialog');
        panel.hidden = true;

        const music = buildChannelRow({
            rowClass: 'audio-channel-row--music',
            muteId: 'music-mute-btn',
            sliderId: 'music-volume-slider',
            labelId: 'music-channel-label',
            labelKey: 'audio.music',
            labelText: t('audio.music', 'Music'),
        });
        const sfx = buildChannelRow({
            rowClass: 'audio-channel-row--sfx',
            muteId: 'sfx-mute-btn',
            sliderId: 'sfx-volume-slider',
            labelId: 'sfx-channel-label',
            labelKey: 'audio.soundEffects',
            labelText: t('audio.soundEffects', 'Sound effects'),
        });

        musicRow = music.row; musicMuteBtn = music.muteBtn; musicMuteGlyph = music.muteGlyph;
        musicSlider = music.slider; musicPct = music.pct;
        sfxRow = sfx.row; sfxMuteBtn = sfx.muteBtn; sfxMuteGlyph = sfx.muteGlyph;
        sfxSlider = sfx.slider; sfxPct = sfx.pct;

        panel.appendChild(music.row);
        panel.appendChild(sfx.row);
        root.appendChild(panel);
        document.body.appendChild(root);

        wireEvents();
        applyStaticLabels();
        refresh();
        installWatchers();
        reposition();

        if (i18n?.translatePage) {
            try { i18n.translatePage(); } catch (_) { }
        }
    }

    /**
     * TAP ANYWHERE ON THE BAR, not only on the thumb.
     *
     * Requested: "volume buttons must work by clicking/tapping anywhere on the
     * bar. not dragging alone." The desktop engines already jump a native
     * `input[type=range]` to a clicked track position; iOS WebKit does not —
     * its slider answers only a drag that begins on the thumb — which is why
     * the report is a phone report. Rather than depend on which widget a
     * platform ships, the value is driven from the pointer here, so the gesture
     * is identical in every engine.
     *
     * The travel is the track MINUS one thumb: a thumb has width and its centre
     * can only reach half a thumb from each end, so a raw `x / width` ratio can
     * never select 0 or 100. Reading the thumb's size from the stylesheet keeps
     * one source of truth for it.
     */
    /**
     * The rendered thumb size, in px.
     *
     * `soundtrack.css` REGISTERS `--mf-audio-slider-thumb` with `@property`, so
     * this reads a computed length. Unregistered, `getPropertyValue` hands back
     * the specified token — for this property the literal string
     * `clamp(14px, 1.25vmin, 24px)` — and `parseFloat` of that is NaN, so every
     * caller silently used the 18px default while the real thumb was 14px on a
     * phone and 24px at 4K. The travel is the track minus one thumb, so that
     * error moved the whole press-to-value mapping.
     */
    function thumbWidthPx(slider) {
        const declared = getComputedStyle(slider)
            .getPropertyValue('--mf-audio-slider-thumb').trim();
        const parsed = Number.parseFloat(declared);
        return Number.isFinite(parsed) && parsed > 0 ? parsed : uiPx(18);
    }

    function valueAtPointer(slider, clientX) {
        const rect = slider.getBoundingClientRect();
        if (!rect.width) return null;
        const min = Number(slider.min);
        const max = Number(slider.max);
        const step = Number(slider.step) || 1;
        if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) return null;
        const thumb = Math.min(thumbWidthPx(slider), rect.width);
        const travel = Math.max(1, rect.width - thumb);
        const ratio = Math.min(1, Math.max(0, (clientX - rect.left - thumb / 2) / travel));
        const stepped = Math.round((min + ratio * (max - min)) / step) * step;
        return String(Math.min(max, Math.max(min, stepped)));
    }

    function installTrackSeek(slider) {
        // Tracked here rather than through pointer capture alone: capture is the
        // right mechanism where it exists, but a browser that has not
        // implemented it must not silently lose the drag half of the gesture.
        let seekingPointer = null;

        /* A TOUCH TAP IS HANDLED TWICE, and the native half wins.
           `preventDefault()` on `pointerdown` stops the widget's POINTER
           handling, but a touch also delivers `touchstart`, and the range input
           acts on that as well — measured at a 932x300 page box: our handler
           emits `input` with the value the press asked for, and a second
           `input` follows from the widget with its own mapping, which is the
           one the player is left with. Suppressing the default touch behaviour
           costs nothing here because `touch-action: none` already opts this
           control out of panning. */
        slider.addEventListener('touchstart', (event) => {
            if (event.cancelable) event.preventDefault();
        }, { passive: false });

        const seekTo = (clientX) => {
            const next = valueAtPointer(slider, clientX);
            if (next === null || slider.value === next) return;
            slider.value = next;
            // The engines are driven by the existing `input` listeners; this
            // adds a way to reach the value, never a second way to apply it.
            slider.dispatchEvent(new Event('input', { bubbles: true }));
        };

        slider.addEventListener('pointerdown', (event) => {
            if (typeof event.button === 'number' && event.button !== 0) return;
            if (valueAtPointer(slider, event.clientX) === null) return;
            seekingPointer = event.pointerId ?? 'mouse';
            // Ours is now the only gesture on this control: without this the
            // native widget also starts its own drag from the thumb's old seat.
            event.preventDefault();
            /* MEASURE BEFORE MOVING ANYTHING. `seekTo` maps the press through
               the bar's CURRENT box, so it has to run before focus or pointer
               capture get a chance to change that box — focusing this control
               shifts it by ~5.6px at a 932x300 page box, which turned a press
               at 12% into 19%. The player aimed at the bar they could see. */
            seekTo(event.clientX);
            try { slider.setPointerCapture?.(event.pointerId); } catch (_) { /* unsupported */ }
            try { slider.focus?.({ preventScroll: true }); } catch (_) { slider.focus?.(); }
        });

        slider.addEventListener('pointermove', (event) => {
            if (seekingPointer === null) return;
            if (seekingPointer !== (event.pointerId ?? 'mouse')) return;
            seekTo(event.clientX);
        });

        const release = (event) => {
            if (seekingPointer === null) return;
            if (seekingPointer !== (event.pointerId ?? 'mouse')) return;
            seekingPointer = null;
            try { slider.releasePointerCapture?.(event.pointerId); } catch (_) { /* unsupported */ }
        };
        slider.addEventListener('pointerup', release);
        slider.addEventListener('pointercancel', release);
        slider.addEventListener('lostpointercapture', release);
        /* The safety net for a release the element never sees. Pointer capture
           normally guarantees it does, but an engine without capture would leave
           the drag latched — and the next stray move across the bar would then
           change the volume nobody touched. */
        window.addEventListener('pointerup', release);
        window.addEventListener('pointercancel', release);
    }

    function wireEvents() {
        toggle.addEventListener('click', () => (isOpen ? close(true) : open()));
        installTrackSeek(musicSlider);
        installTrackSeek(sfxSlider);

        musicSlider.addEventListener('input', () => {
            const s = soundtrack();
            if (s) s.setMusicVolume(Number(musicSlider.value) / 100);
        });
        sfxSlider.addEventListener('input', () => {
            const s = soundEffects();
            if (s) s.setVolume(Number(sfxSlider.value) / 100);
        });
        musicMuteBtn.addEventListener('click', () => {
            const s = soundtrack();
            if (s) s.toggleMute();
        });
        sfxMuteBtn.addEventListener('click', () => {
            const s = soundEffects();
            if (!s) return;
            s.toggleMuted();
            /* The shared press cue fires BEFORE this handler (capture phase),
               so muting is heard as one last tick and then silence — but
               unmuting found the channel still muted and played nothing. The
               tick after the toggle is the confirmation the player is owed:
               "sound is back". Muting takes the early path and skips it. */
            if (!s.isMuted()) s.play('press');
        });

        // Keep the UI mirrored to authoritative engine state.
        document.addEventListener('moonfall:music-changed', refresh);
        document.addEventListener('moonfall:sfx-changed', refresh);

        // Re-localize on language change.
        window.addEventListener('moonfall:i18nready', onLanguageChange);
        window.addEventListener('moonfall:languagechange', onLanguageChange);
    }

    function onLanguageChange() {
        applyStaticLabels();
        refresh();
        if (i18n?.translatePage) {
            try { i18n.translatePage(); } catch (_) { }
        }
    }

    function applyStaticLabels() {
        /* The trigger opens the whole menu, not the audio settings, so it is
           named for what it opens. The PANEL keeps a menu name too — a dialog
           announced as "Audio settings" that also contains Language, Feedback
           and Full screen mis-describes itself to a screen reader. */
        const label = t('menu.open', 'Menu');
        toggle.setAttribute('aria-label', label);
        toggle.setAttribute('title', label);
        panel.setAttribute('aria-label', t('menu.title', 'Menu'));
        musicSlider.setAttribute('aria-label', t('audio.musicVolume', 'Music volume'));
        sfxSlider.setAttribute('aria-label', t('audio.sfxVolume', 'Sound effects volume'));
    }

    function refreshChannel({ slider, pct, muteBtn, muteGlyph, row, raw, muted, effective, levelKey, muteKey, unmuteKey }) {
        const rawPct = toPct(raw);
        if (document.activeElement !== slider) slider.value = String(rawPct);
        pct.textContent = `${rawPct}%`;

        row.classList.toggle('audio-channel-row--muted', muted);
        muteBtn.setAttribute('aria-pressed', String(muted));
        muteGlyph.textContent = '';
        muteGlyph.appendChild(speakerGlyph(muted ? 0 : effective));
        muteBtn.setAttribute('aria-label', muted ? t(unmuteKey.key, unmuteKey.fallback) : t(muteKey.key, muteKey.fallback));

        const valueText = t(levelKey, `${rawPct}%`, { percent: rawPct });
        slider.setAttribute('aria-valuetext', valueText);
    }

    function refresh() {
        if (!root) return;
        refreshChannel({
            slider: musicSlider, pct: musicPct, muteBtn: musicMuteBtn, muteGlyph: musicMuteGlyph, row: musicRow,
            raw: musicRaw(), muted: musicMuted(), effective: musicEffective(),
            levelKey: 'audio.musicLevel',
            muteKey: { key: 'audio.muteMusic', fallback: 'Mute music' },
            unmuteKey: { key: 'audio.unmuteMusic', fallback: 'Unmute music' },
        });
        refreshChannel({
            slider: sfxSlider, pct: sfxPct, muteBtn: sfxMuteBtn, muteGlyph: sfxMuteGlyph, row: sfxRow,
            raw: sfxRaw(), muted: sfxMuted(), effective: sfxEffective(),
            levelKey: 'audio.sfxLevel',
            muteKey: { key: 'audio.muteSfx', fallback: 'Mute sound effects' },
            unmuteKey: { key: 'audio.unmuteSfx', fallback: 'Unmute sound effects' },
        });

        /* The collapsed trigger used to BE the volume readout — its emoji
           stepped through 🔇🔈🔉🔊 with the louder channel. It is the menu now,
           so the icon stays the menu's icon and only the fact of silence is
           surfaced, as an attribute the stylesheet dims the sigil on. A player
           who has muted everything still gets a tell without the menu button
           pretending to be a speaker. */
        const combined = Math.max(musicEffective(), sfxEffective());
        if (combined <= 0) root.dataset.muted = 'true';
        else delete root.dataset.muted;
    }

    // --- open / close ---
    function onDocumentPointerDown(event) {
        if (root && root.contains(event.target)) return;
        close(false);
    }
    function onDocumentKeydown(event) {
        if (event.key === 'Escape') {
            event.stopPropagation();
            close(true);
        }
    }

    function open() {
        if (isOpen || !panel) return;
        isOpen = true;
        panel.hidden = false;
        toggle.setAttribute('aria-expanded', 'true');
        positionPanel();
        document.addEventListener('pointerdown', onDocumentPointerDown, true);
        document.addEventListener('keydown', onDocumentKeydown, true);
        /* NEVER SCROLL AN ANCESTOR TO REVEAL THE SLIDER. The panel is placed
           by `positionPanel()`, so nothing has to be scrolled into view — and
           where the menu is seated inside another control's row (the Foundry's
           tab bar), a plain focus scrolled that row's `overflow: hidden` box
           by 68px and carried the tabs, the Stardust pill and the menu itself
           off the top of a 300px landscape page box. */
        try { musicSlider.focus({ preventScroll: true }); } catch (_) { }
    }

    function close(returnFocus) {
        if (!isOpen || !panel) return;
        isOpen = false;
        panel.hidden = true;
        toggle.setAttribute('aria-expanded', 'false');
        document.removeEventListener('pointerdown', onDocumentPointerDown, true);
        document.removeEventListener('keydown', onDocumentKeydown, true);
        if (returnFocus) { try { toggle.focus({ preventScroll: true }); } catch (_) { } }
    }

    function toggleOpen() { return isOpen ? close(true) : open(); }

    // --- placement ---
    // In battle the sliders open above the entire right-hand control stack.
    // Elsewhere, open below the trigger or above it when room is tight.
    function positionPanel() {
        if (!isOpen || !panel || !root) return;
        const r = root.getBoundingClientRect();
        const ph = panel.offsetHeight || 0;
        const pw = panel.offsetWidth || 0;
        const gap = vmin();
        const safe = safeAreaInsets();

        const skip = document.getElementById('skip-tutorial-btn');
        const skipRect = skip?.getBoundingClientRect?.();
        const tutorialTop = skipRect && skipRect.width > 0 && skipRect.height > 0
            ? Math.min(skipRect.top, r.top) - gap - ph
            : null;
        const anchorToTutorial = document.body.classList.contains('game-active')
            && tutorialTop !== null
            && tutorialTop >= safe.top + gap;

        panel.classList.toggle('audio-settings-panel--tutorial', anchorToTutorial);
        if (anchorToTutorial) {
            panel.classList.remove('audio-settings-panel--above', 'audio-settings-panel--below');
            // Floor toward the top of the viewport, then retain two physical
            // CSS pixels of guard space. Chromium can report the final panel
            // border-box fractionally taller and the transformed trigger a
            // fraction higher than their integer layout boxes; this keeps the
            // promised full gap above both controls after compositing.
            panel.style.top = `${Math.floor(tutorialTop - r.top) - 2}px`;
            panel.style.bottom = 'auto';
        } else {
            panel.style.removeProperty('top');
            panel.style.removeProperty('bottom');
            const roomBelow = window.innerHeight - safe.bottom - r.bottom;
            const above = roomBelow < ph + gap && r.top - safe.top > ph + gap;
            panel.classList.toggle('audio-settings-panel--above', above);
            panel.classList.toggle('audio-settings-panel--below', !above);
            if (!above && document.body.classList.contains('foundry-surface-active')) {
                const commandBar = document.querySelector('.foundry-tabs');
                const commandBarRect = commandBar?.getBoundingClientRect?.();
                if (commandBarRect?.height > 0) {
                    panel.style.top = `${Math.ceil(commandBarRect.bottom + gap - r.top)}px`;
                    panel.style.bottom = 'auto';
                }
            }
        }

        const triggerCenter = r.left + r.width / 2;
        let left = triggerCenter - pw / 2;
        const minLeft = safe.left + gap;
        const maxLeft = Math.max(minLeft, window.innerWidth - safe.right - pw - gap);
        left = Math.max(minLeft, Math.min(left, maxLeft));
        panel.style.left = `${Math.round(left - r.left)}px`;
        panel.style.right = 'auto';
    }

    /**
     * The rectangle every obstacle set yields.
     *
     * TYPED STRUCTURALLY, NOT AS `DOMRect`, because the sets are concatenated
     * into one list and not all of them come from `getBoundingClientRect`:
     * battleContentRects() synthesises the storyboard's full-height COLUMN
     * rather than its current box, and that literal has no `x`, `y` or
     * `toJSON`. A `DOMRect[]` chain cannot absorb it, so the whole
     * `.concat()` ladder is a list of this shape — which every DOMRect
     * already satisfies. All this code ever reads is the six edges.
     *
     * @typedef {{
     *   left: number, right: number, top: number, bottom: number,
     *   width: number, height: number,
     * }} ObstacleRect
     */

    /** @param {ObstacleRect | DOMRect | null | undefined} rect */
    function rectHasArea(rect) {
        return !!rect && rect.width > 0 && rect.height > 0;
    }

    function rectsOverlap(first, second) {
        return rectHasArea(first)
            && rectHasArea(second)
            && first.left < second.right
            && first.right > second.left
            && first.top < second.bottom
            && first.bottom > second.top;
    }

    /**
     * Keep the End Turn + exit group inside the free band BETWEEN the two
     * summoner clusters.
     *
     * The button's CSS seat is the viewport midpoint less half the exit band,
     * which assumes both clusters are the same height. A summoner nameplate is
     * a fixed box, but the cluster around it is not: a name that balances onto
     * two lines (Sylva) hangs the opponent's crest further down the lane the
     * rail shares, and the button landed on it — "the end turn button is
     * overlapping the top summoner name". Measure the band instead of assuming
     * it, and only move the button when it genuinely does not fit.
     */
    /* The countdown rides End Turn: where the solve leaves the button, the
       anchor follows, written once and only when it changes (a custom property
       on the page root restyles the whole document, round 15). */
    function seatEndTurnBetweenSummoners(endTurn) {
        const seated = solveEndTurnSeat(endTurn);
        if (seated) publishTurnTimerAnchor(seated.top, seated.height);
        else clearTurnTimerAnchor();
    }

    function setDatasetValue(element, key, value) {
        if (value === null || value === undefined) {
            if (key in element.dataset) delete element.dataset[key];
        } else if (element.dataset[key] !== value) {
            element.dataset[key] = value;
        }
    }

    /* Where a written `top` lands: the button is translated (translateY(-50%),
       and the turn-state animation re-declares that transform), so its box
       sits `offset` px from the `top` it is given. Read with the CSS seat's own
       measure, before anything is written, so the seat is written once and
       never read back (round 15: a write then a read is a forced layout).
       null where the engine does not resolve `top` to pixels. */
    function landedOffset(endTurn, rect) {
        try {
            const cssTop = Number.parseFloat(getComputedStyle(endTurn).top);
            return Number.isFinite(cssTop) ? rect.top - cssTop : null;
        } catch (_) {
            return null;
        }
    }

    /** @returns {{top: number, height: number}|null} the seat written, or null on the CSS seat */
    function solveEndTurnSeat(endTurn) {
        /* Reset the LANE as well as the seat. A previous frame may have stepped
           the pair outboard; measuring from that displaced position would let
           it walk further left on every reposition. Only what is there is
           reset: an empty property written empty is still a mutation. */
        if (endTurn.style.top) endTurn.style.top = '';
        if (endTurn.style.left) endTurn.style.left = '';
        if (endTurn.style.right) endTurn.style.right = '';
        setDatasetValue(endTurn, 'commandLane', null);
        /* The countdown's anchor is NOT cleared to measure: End Turn's seat
           does not depend on it, and clearing then re-setting a custom property
           on the page root restyled the whole battle twice per solve (30-55 ms
           at 4x CPU, round 15). seatEndTurnBetweenSummoners settles it once. */
        const rect = endTurn.getBoundingClientRect();
        if (!rectHasArea(rect)) return null;
        const offset = landedOffset(endTurn, rect);

        /* A MINIMAL correction, never a re-centring.
           The CSS seat already centres End Turn and Audio as one pair on the
           viewport midpoint, and that is the arrangement — re-deriving a
           "better" centre here moved the button even when nothing was in its
           way and broke the pair. Only a real collision earns a move, and only
           by the distance needed to clear it.

           But the move must be VERIFIED, which is what this used to get wrong.
           It resolved each crest independently — max() for one above, min() for
           one below — so with crests on BOTH sides the second decision silently
           undid the first. Measured on an iPhone 13 at 844x280: the CSS seat
           painted 93.9-137.9, hitting the opponent portrait (0-98.8) AND its
           nameplate (101.5-132.7); the portrait pushed it down to 101.0, the
           nameplate then pushed it up to 55.3, and it landed on the portrait it
           had just cleared — "end turn buttn overlaps enemy summoner", 42.8px
           of it. Nothing ever checked the seat that was emitted.

           So: enumerate candidate seats, keep only the ones that collide with
           nothing, and take the one closest to the CSS seat. */
        const gap = vmin() * 0.8;
        const safe = safeAreaInsets();

        /* SEAT THE PAIR, NOT THE BUTTON.

           The volume trigger is rigidly stacked above End Turn, so the thing
           that occupies the lane is a TALLER box than this button: End Turn plus
           the trigger plus the gap between them. Solving for the button alone
           seats a box that does not exist, and whatever sits in the band above
           gets covered by the trigger with every collision check reporting
           clear.

           That is not hypothetical — it regressed six page boxes the moment the
           trigger moved above End Turn. Measured at 1366x768: the opponent's
           status ribbon at 1064.9..1344.9 x 254.0..292.8 against the trigger at
           1138..1172 x 286..320. The ledger already required this: "End Turn and
           Audio are measured as one 44px command pair and their combined
           box—not End Turn alone—owns the vertical midpoint between clusters."

           The STATUS RIBBONS join the obstacle set here for the same reason.
           They are information — stacks, turns, the effect about to resolve —
           and they live in exactly the band the trigger now claims. */
        /* The pair is HORIZONTAL, so it reserves no band above End Turn — that
           is the whole reason it is horizontal. See the placement note below. */
        const band = 0;

        const lo = safe.top + gap + band;
        const hi = window.innerHeight - safe.bottom - rect.height - gap;
        if (!(hi > lo)) return null;

        const inColumn = (o) => rect.left < o.right && rect.right > o.left;
        const profiles = battleProfileRects()
            .concat(battleStatusRects())
            .filter(inColumn);
        // `top` is End Turn's top; the pair reaches `band` px higher.
        const clearOf = (top) => !profiles.some(
            (p) => top - band < p.bottom && top + rect.height > p.top,
        );
        /* A SEAM THAT CAN HOLD THE CONTROL EARNS THE CONTROL'S CENTRE.

           This used to enumerate each obstacle's edges and take the viable one
           NEAREST the CSS seat — a minimal correction, never a re-centring. That
           was the right rule while the seam could not hold the button at all:
           the clusters once owned 0-132.7 and 147.3-280, leaving 14.6px for a
           44px control, so every seat was a compromise and moving least was the
           honest choice.

           The crest shrank and the volume trigger left the lane, so the seam now
           holds the button outright — and "move the least distance" then stops
           the instant it clears the enemy nameplate. Measured on the owner's
           iPhone 13 at 844x280 (insets 47/47/0/20, read over the USB inspector):
           the clusters end at 112.2 and resume at 167.8, a 55.6px seam, and the
           button seated at 115-159 — 2.8px of clearance above against 8.8px
           below. The centre error is 3px; the asymmetry is three to one, and it
           reads as a button hugging the opponent's nameplate. Reported as "rn
           that end turn button is nto centered with the summoner icons".

           So solve for the free INTERVAL rather than for an edge. A seat `top`
           is forbidden by obstacle `p` exactly when it lies in
           `(p.top - height - gap, p.bottom + gap)`; merge those, subtract from
           `[lo, hi]`, and what remains is every seam this column offers, already
           carrying the required clearance. Take the one nearest the CSS seat and
           sit in the middle of it. Centring is now the arithmetic rather than a
           correction applied on top of it. */
        const forbidden = profiles
            .map((p) => [p.top - rect.height - gap, p.bottom + gap - band])
            .sort((a, b) => a[0] - b[0]);

        const free = [];
        let cursor = lo;
        for (const [from, to] of forbidden) {
            if (from > cursor) free.push([cursor, Math.min(from, hi)]);
            cursor = Math.max(cursor, to);
            if (cursor >= hi) break;
        }
        if (cursor < hi) free.push([cursor, hi]);

        /* A zero-width seam is a coincidence, not a seat: it means the button
           fits only if it touches both neighbours, which is what the gap exists
           to prevent. Distance is measured to the interval, not to its middle,
           so a seam the CSS seat already sits inside always wins. */
        const seams = free
            .filter(([from, to]) => to > from)
            .sort((a, b) => (
                Math.max(a[0] - rect.top, 0, rect.top - a[1])
                - Math.max(b[0] - rect.top, 0, rect.top - b[1])
            ));

        /* No seam in this column clears everything. Rather than pick the
           least-bad overlap, step the pair OUT of the portrait lane;
           audio-settings re-centres the trigger on End Turn, so the pair moves
           together. Verified against the same obstacle set in its new lane. */
        if (!seams.length) {
            return seatEndTurnOutboard(endTurn, rect, { gap, lo, hi, band, offset });
        }
        const seated = (seams[0][0] + seams[0][1]) / 2;
        /* The interval arithmetic is the contract, but the emitted seat is what
           the player sees — verify it the way the old code never did. */
        if (!clearOf(seated)) {
            return seatEndTurnOutboard(endTurn, rect, { gap, lo, hi, band, offset });
        }
        if (Math.abs(seated - rect.top) < 1) return null;
        return writeSeat(endTurn, seated, rect, offset);
    }

    /**
     * Last resort: step the command pair out of the summoner lane entirely.
     *
     * Only reached when no seat in the declared column clears every crest —
     * which at 844x280 is not a tuning failure but arithmetic: the clusters own
     * 0-132.7 and 147.3-280, leaving a 14.6px seam for a 44px control. Sitting
     * on a portrait is the one outcome that is never acceptable, so the lane
     * yields instead of the collision.
     */
    /* `band` is the space the volume trigger occupies ABOVE End Turn. The
       outboard path must reserve it for exactly the same reason the in-lane
       path does: the pair moves as one object, so solving for the button alone
       seats a box that does not exist and the trigger lands on whatever is in
       the band above. Out here that is the board and the summoner status
       ribbons, and it is what put the trigger 1.25px into an opponent ribbon on
       the boxes tight enough to take this fallback. */
    function seatEndTurnOutboard(endTurn, rect, { gap, lo, hi, band = 0, offset = null }) {
        const safe = safeAreaInsets();
        const minLeft = safe.left + gap;
        /* EVERYTHING is an obstacle out here, not just the summoner crests.
           Stepping out of the declared lane means crossing the BOARD, and the
           first version of this searched `battleProfileRects()` alone — so the
           pair cleared the portraits by landing on the slots and the narration
           lane instead: "vol buttn covers slots", "lobby button is in the way
           of storyboard squares". A control that steals a drop target or hides
           the story is worse than one that sits somewhere less pretty, which is
           the same rule battleProfileRects() already documents for the decks.
           The Return control's own search has always used this full set; the
           outboard path must use the identical one or the two disagree about
           what counts as an obstacle. */
        const all = battleProfileRects()
            .concat(battleSlotRects())
            .concat(battleBoardCardRects())
            .concat(battleContentRects())
            .concat(battleStatusRects());
        const preferredTop = Math.max(lo, Math.min(rect.top, hi));

        for (let left = rect.left - rect.width - gap; left >= minLeft; left -= rect.width + gap) {
            const here = all.filter((p) => left < p.right && left + rect.width > p.left);
            // `top` is End Turn's top; the pair reaches `band` px higher.
            const clearOf = (top) => !here.some(
                (p) => top - band < p.bottom && top + rect.height > p.top,
            );
            const tops = [preferredTop, lo, hi]
                .concat(here.flatMap((p) => [p.bottom + gap + band, p.top - gap - rect.height]))
                .filter((top) => top >= lo && top <= hi && clearOf(top))
                .sort((a, b) => Math.abs(a - preferredTop) - Math.abs(b - preferredTop));
            if (!tops.length) continue;

            endTurn.dataset.commandLane = 'outboard';
            endTurn.style.right = 'auto';
            endTurn.style.left = `${Math.round(left)}px`;
            return writeSeat(endTurn, tops[0], rect, offset);
        }
        /* Nowhere clear anywhere on the row: leave the declared seat rather
           than inventing a worse one, and say so — a silent fallback here is
           how a layout regression reaches a player looking healthy. */
        console.warn('[AudioSettings] No collision-free seat for End Turn; keeping the CSS seat.');
        return null;
    }

    /**
     * Write a seat and correct for whatever is transforming the element.
     *
     * `top` is not where the button LANDS: the base rule centres it with
     * `translateY(-50%)` and the turn-state animation re-declares that transform
     * through the Web Animations API, which overrides inline style outright. So
     * measure where it actually went and add the residual back.
     */
    /* The turn countdown rides one gap above End Turn, and its CSS expresses
       that as a SIBLING of the button's own CSS seat — which holds right up
       until THIS module moves the button off that seat. When it does, the
       countdown stays behind and the two collide: measured at 768x1024, the
       button was re-seated to 307-364 while the pill stayed at 408-449, and a
       last-chance pill (taller by its warning line) reached 375 and landed on
       the button. So whoever moves the button republishes the anchor.

       The published value is the pill's BOTTOM (it carries translateY(-100%)),
       kept at exactly the offset the CSS would have produced: the button's
       CENTRE less 4.6vmin. Absent, the CSS falls back to its own seat, so a
       frame where this never runs behaves exactly as before. */
    const TIMER_BOTTOM_BELOW_END_TURN_CENTRE_VMIN = 4.6;

    /* From the seat the solve wrote (where the button's box now starts), not
       from a read of the button after writing it. */
    function publishTurnTimerAnchor(top, height) {
        if (!(height > 0)) return;
        const bottom = top + height / 2
            - TIMER_BOTTOM_BELOW_END_TURN_CENTRE_VMIN * vmin();
        const value = `${Math.round(bottom)}px`;
        const rootStyle = document.documentElement.style;
        if (rootStyle.getPropertyValue('--turn-timer-anchor-top') === value) return;
        rootStyle.setProperty('--turn-timer-anchor-top', value);
    }

    function clearTurnTimerAnchor() {
        const rootStyle = document.documentElement.style;
        if (!rootStyle.getPropertyValue('--turn-timer-anchor-top')) return;
        rootStyle.removeProperty('--turn-timer-anchor-top');
    }

    /** @returns {{top: number, height: number}} where the button's box now starts */
    function writeSeat(endTurn, seated, rect, offset = null) {
        if (Number.isFinite(offset)) {
            // Written once, already corrected for the transform (landedOffset).
            endTurn.style.top = `${Math.round(seated - offset)}px`;
            return { top: seated, height: rect.height };
        }
        endTurn.style.top = `${Math.round(seated)}px`;
        const landed = endTurn.getBoundingClientRect();
        const residual = seated - landed.top;
        if (Math.abs(residual) > 0.5) {
            endTurn.style.top = `${Math.round(seated + residual)}px`;
        }
        return { top: seated, height: rect.height };
    }

    /**
     * Is this box a legal seat for the Return control?
     *
     * Shared by the pin check and the candidate search so the two can never
     * disagree about what counts as an obstacle. The status ribbons belong in
     * EVERY seat predicate, not just the declared lane's.
     */
    function returnSeatIsClear(box, { obstacles, audioRect, etRect, safe }) {
        return !obstacles.some((o) => rectsOverlap(box, o))
            && !rectsOverlap(box, audioRect)
            && !rectsOverlap(box, etRect)
            && box.top >= safe.top
            && box.bottom <= window.innerHeight - safe.bottom;
    }

    /** @returns {ObstacleRect[]} */
    function battleProfileRects() {
        return [
            'player-portrait',
            'opponent-portrait',
            'player-health-indicator',
            'player-mana-indicator',
            'opponent-health-indicator',
            'opponent-mana-indicator',
            'player-status-indicator',
            'opponent-status-indicator',
            /* The DECKS are drop targets, not decoration. They were missing
               here, so the volume control could be placed straight onto one and
               swallow its drop area — and on a phone the deck is already the
               tightest target in the battle. A control that steals a drop zone
               is worse than a control that sits somewhere less pretty. */
            'player-deck',
            'opponent-deck',
        ].map((id) => document.getElementById(id)?.getBoundingClientRect?.())
            /* The NAMEPLATES are part of the summoner cluster too, and they were
               listed here by ID — `player-summoner-name` / `opponent-summoner-name`
               — which nothing has ever been called. indicators.js builds them
               with the CLASS `summoner-nameplate` and no id, so both lookups
               returned null and the plates were never obstacles at all, while a
               comment claimed they were. Measured live in a tutorial battle at
               844x288: the two-line "Sylva Thornheart" plate occupied 75-99 and
               End Turn sat at 79-123, straight through it. Query the class. */
            .concat([...document.querySelectorAll('.summoner-nameplate')]
                .filter((el) => !el.hidden)
                .map((el) => el.getBoundingClientRect()))
            .filter(rectHasArea);
    }

    /* The BOARD is content too. Moving the exit control off a nameplate and into
       the middle band printed it straight across the player's own card slots —
       the band between the two summoner clusters is the play area, not spare
       room. Slots are measured rather than assumed because their count and
       position change with the battle. */
    /** @returns {ObstacleRect[]} */
    function battleSlotRects() {
        return [...document.querySelectorAll('.card-slot')]
            .map((el) => el.getBoundingClientRect())
            .filter(rectHasArea);
    }

    /* SUMMONED CARDS, both boards.

       These were in NO obstacle set, and that single omission is why the owner's
       report ("the summoned card slots are overlapping it") kept coming back
       while every collision check reported clear. A summoned card is drawn
       LARGER than the slot that holds it — it overhangs on all four sides — so
       asking only `.card-slot` measures the hole, not the thing in it.

       Kept even though the volume trigger is now rigidly stacked and no longer
       searches: this is the check that would have caught the defect the first
       time, and the exit search on tablet and desktop still consults it. */
    /** @returns {ObstacleRect[]} */
    function battleBoardCardRects() {
        return [...document.querySelectorAll('#player-board .card, #opponent-board .card')]
            /* A CARD IN MOTION IS NOT WHERE IT STANDS. A lunge, a reach or a
               knock-back carries the card across the table on its mover
               (fx.js `moveCard`, which marks the slot `fx-slot-lifted` for the
               run), and a pinned seat checked against a card mid-flight was
               surrendered for the rest of the match: in a real first session
               (2026-10-17) the enemy's lunge at Orion's portrait crossed the
               tutorial's Skip under End Turn and re-homed it to the board's
               bottom-left. The card's slot stays an obstacle the whole time
               (battleSlotRects), and the card counts again once it is home. */
            .filter((el) => !el.closest('.card-slot')?.classList.contains('fx-slot-lifted'))
            .map((el) => el.getBoundingClientRect())
            .filter(rectHasArea);
    }

    /* Hands and the narration lane are content as well. Each time this control
       was placed by picking a spot that looked free, it landed on whatever was
       not in the obstacle list — the nameplate, then the board, then the hand.
       Listing everything and SEARCHING ends that. */
    /* The summoner status ribbons. They are INFORMATION — stacks, turns, the
       effect that is about to resolve — so a control may never sit on one. They
       are deliberately NOT in battleProfileRects(): the lane aligns to the
       portraits, and treating the ribbon as an alignment anchor would drag the
       column off the portraits it is supposed to match. It is an obstacle, not
       an anchor. */
    /** @returns {ObstacleRect[]} */
    function battleStatusRects() {
        const rects = [];
        for (const el of document.querySelectorAll('.status-indicator')) {
            if (el.style.display === 'none') continue;
            const rect = el.getBoundingClientRect();
            if (rectHasArea(rect)) rects.push(rect);
        }
        return rects;
    }

    /* The dev-only concede (DEBUG_MODE) USED to be fixed under End Turn by CSS,
       in the exact band the exit's seat search likes best — and it was not in
       the obstacle set, so the solver seated the exit straight on top of it and
       the debug button became unclickable ("Go to Lobby intercepts pointer
       events"). `chrome-menu.js` adopts it into the menu now, so in an ordinary
       build it is either `display: none` (production) or a row inside a closed
       panel; either way this returns nothing.

       The panel check is the same one `reposition()` runs for the exit: a
       control inside the menu is not rail furniture, and counting an OPEN
       panel's rows as obstacles would make the exit hop every time a player
       opened the menu mid-match. The lookup survives for the orphaned case —
       if adoption ever failed the button renders at its static position inside
       `#game-board-middle`, which IS near the rail. The dev win button is the
       concede's sibling and is treated identically.
       @returns {ObstacleRect[]} */
    function battleDebugControlRects() {
        const rects = [];
        for (const id of ['debug-end-match-btn', 'debug-win-match-btn', 'debug-reveal-hand-btn', 'debug-board-reveal-btn']) {
            const el = document.getElementById(id);
            if (!el || el.closest('#audio-settings-panel')) continue;
            if (getComputedStyle(el).display === 'none') continue;
            const rect = el.getBoundingClientRect();
            if (rectHasArea(rect)) rects.push(rect);
        }
        return rects;
    }

    /* THE COUNTDOWN CHIP IS THE FOURTH MEMBER OF THIS LANE, and it was in no
       obstacle set at all.

       `#turn-timer` and `#mulligan-timer` (which mulligan.css styles by its
       `.mulligan-timer` class) are pinned to End Turn's own column
       (`--end-turn-right`, `--end-turn-width`) one gap ABOVE the button, and
       the exit used to offer exactly that band as its second seat. Nothing
       checked, so the collision was accepted in silence: measured in the guest
       tutorial's mulligan on Chromium, 6037px² at 1920x1080, 3593 at 1366x768,
       2467 at 1400x640, 19137 at 3840x2160 — Skip printed straight over the
       countdown, hiding the number entirely. Reported as "skip tutorial button
       overlaps mulligan timer on my desktop monitor".

       The two chips are never on screen together (the mulligan resolves before
       turn one exists), so listing both costs one null lookup.
       @returns {ObstacleRect[]} */
    function battleCountdownRects() {
        const rects = /** @type {ObstacleRect[]} */ ([]);
        for (const id of ['turn-timer', 'mulligan-timer']) {
            const el = document.getElementById(id);
            if (!el || el.hidden) continue;
            const style = getComputedStyle(el);
            if (style.display === 'none' || style.visibility === 'hidden') continue;
            const rect = el.getBoundingClientRect();
            if (rectHasArea(rect)) rects.push(rect);
        }
        return rects;
    }

    /** @returns {ObstacleRect[]} */
    function battleContentRects() {
        const rects = /** @type {ObstacleRect[]} */ ([]);
        const surfaces = [
            document.getElementById('player-hand'),
            document.getElementById('opponent-hand'),
            document.querySelector('.tutorial-tip'),
        ];

        /* THE STORYBOARD IS A FULL-HEIGHT COLUMN, NOT ITS CURRENT BOX.

           Owner's rule, verbatim: the exit "must not under any circustance sit
           undreneath the storyboard squares. those have limited space and must
           go all the way down."

           Reserving only the feed's CURRENT rect satisfies that for exactly as
           long as the feed is short. It starts a match ~114px tall and grows as
           narration and manga panels land, so the solver was free to seat the
           exit directly beneath it — measured at 844x288: feed 8..122, exit
           161..205, both at x~4. That is the forbidden position, and it also
           means the feed grows straight into a control that paints below it
           (feed z-index 9535 against 9450), so it covers rather than reflows.

           The column is what has limited space. Reserve its full height. */
        const feed = document.getElementById('story-feed');
        const feedRect = feed?.getBoundingClientRect?.();
        if (rectHasArea(feedRect)) {
            rects.push({
                left: feedRect.left,
                right: feedRect.right,
                top: 0,
                bottom: window.innerHeight,
                width: feedRect.width,
                height: window.innerHeight,
            });
        }
        for (const el of surfaces) {
            const rect = el?.getBoundingClientRect?.();
            if (rectHasArea(rect)) rects.push(rect);
            /* A hand CONTAINER is a thin strip hugging the viewport edge; the
               cards it holds visibly rise above it (measured at 844x288:
               container top 262, card tops ~218). A responsive layout may dock
               that arch near the free column that owns this control's home.
               The container alone understates where the hand actually is —
               measure the cards too. */
            if (el?.id === 'player-hand' || el?.id === 'opponent-hand') {
                for (const card of el.querySelectorAll('.card')) {
                    const cardRect = card.getBoundingClientRect();
                    if (rectHasArea(cardRect)) rects.push(cardRect);
                }
            }
        }
        return rects;
    }

    /* THE EXIT'S SEAT IS PINNED FOR THE MATCH. Every obstacle set above is
       partly LIVE — the story feed grows with narration, status ribbons come
       and go, summons fill slots — and a seat re-derived from live obstacles
       hops with them, which the owner rejected outright ("it moves around
       during a game depending on game state. this is unacceptable"). Once a
       seat is taken it holds; only a viewport/safe-area change (the geometry
       the seat was solved against) or a hand card genuinely covering it
       (interactive surfaces stay uncoverable, the one non-negotiable) may
       re-seat. Cleared on leaving the game. */
    /* One remembered seat PER GEOMETRY, never overwritten during a match:
       rotating away (or the keyboard retracting the browser bars) changes the
       viewport, and a single-slot pin re-solved on the way out AND on the way
       back, landing somewhere new each time. A Map keyed by geometry means
       every landscape box owns exactly one seat for the whole game. Bounded:
       a match sees a handful of geometries at most. */
    const returnSeatPins = new Map();
    /* One-shot guard for the command-lane re-seat below: a layout that cannot
       settle must not recurse. */
    let reseatingCommandLane = false;
    const RETURN_SEAT_PIN_CAP = 6;

    function handCardRects() {
        const rects = [];
        for (const id of ['player-hand', 'opponent-hand']) {
            const hand = document.getElementById(id);
            if (!hand) continue;
            for (const card of hand.querySelectorAll('.card')) {
                const rect = card.getBoundingClientRect();
                if (rectHasArea(rect)) rects.push(rect);
            }
        }
        return rects;
    }

    // One owner positions the stable battle command rail on every viewport.
    // Audio normally shares End Turn's centre line below the button. If that
    // physical box would touch either summoner cluster, it moves beside End
    // Turn instead; profile art and resource sockets are never valid anchors.
    /* `seatMenuClearOfEnemyPortrait()` used to live here.

       It read the resting corner, lifted the trigger above the enemy
       summoner's portrait, and stepped inboard where a square page box left no
       room above. It is deleted because the owner asked for the opposite: one
       seat, identical in the lobby and in a match, with equal top and right
       insets. Chrome that relocates itself between screens is chrome a player
       has to re-find, and the lift is what made the corner non-square. */

    /* THE TRIGGER GOES BACK TO ITS CSS CORNER: every inline coordinate a
       battle solve may have left on it is dropped.

       Except a SEAT. While the Foundry is open chrome-menu.js stands the
       same root on that surface's tab bar and marks it `data-menu-seat`;
       those coordinates are that module's, measured from the bar, and this
       solve — which runs on every translation pass and resize — used to wipe
       them. The menu then fell to the lobby corner under the Foundry's × and
       stayed there, because nothing it listens to had changed. */
    function releaseCorner(el) {
        if (el.dataset.menuSeat) return;
        el.style.left = '';
        el.style.top = '';
        el.style.right = '';
        el.style.bottom = '';
        el.style.transform = '';
        delete el.dataset.layout;
    }

    function reposition() {
        const el = document.getElementById(ROOT_ID);
        if (!el) return;
        const returnControl = document.getElementById('skip-tutorial-btn');
        const endTurn = document.getElementById('end-turn-btn');

        const inGame = document.body.classList.contains('game-active');
        /* While the rotate overlay owns the screen the layout underneath is
           transient garbage — nothing may re-seat against it ("when vertical
           I have the overlay and cannot play but it still somehow affects
           the game"). Everything stays exactly as the last landscape solve
           left it until the overlay yields. */
        if (inGame) {
            const rotateOverlay = document.getElementById('rotate-overlay');
            if (rotateOverlay
                && rotateOverlay.getAttribute('aria-hidden') === 'false') {
                return;
            }
        }
        if (!inGame) {
            returnSeatPins.clear();
            releaseCorner(el);
            if (endTurn) {
                delete endTurn.dataset.audioLayout;
                delete endTurn.dataset.returnLayout;
                endTurn.style.top = '';
            }
            clearTurnTimerAnchor();
            if (returnControl) {
                returnControl.style.left = '';
                returnControl.style.top = '';
                returnControl.style.right = '';
                returnControl.style.bottom = '';
                returnControl.style.transform = '';
                delete returnControl.dataset.layout;
            }
            if (isOpen) positionPanel();
            return;
        }

        if (!endTurn) return;
        /* Measure the preferred vertical pair first. Narrow phone CSS uses this
           marker to reserve half of Audio's height below the viewport midpoint.
           If that physical box hits a summoner cluster, switch to the horizontal
           layout and remeasure End Turn at the true midpoint before positioning
           Audio. This makes the painted group—not one arbitrary member—central. */
        const compactRail = usesCompactRail();
        /* Declare the compact arrangement BEFORE measuring. Return to Lobby
           takes the band under End Turn, so End Turn shifts up by half that
           band and the PAIR ends up centred between the summoners — the same
           trick the old below-Audio layout used. Measuring first and shifting
           after would place both controls against a stale box, and on a 300px
           page box the 29px left under End Turn is not enough for a 44px
           control, so Return would fall through to the far column again. */
        /* The corrective pass below re-enters here having just proved the exit
           cannot take that band; re-declaring it would undo the correction and
           put the lane straight back where it was. */
        /* WHO TAKES THE BAND IS KNOWN BEFORE THE MEASURE (round 15, 28
           September 2026). On a phone rail the exit is in the corner and on a
           match whose exit lives in the menu there is no exit on the rail at
           all: End Turn used to wear the band for the measure and take it off
           again afterwards (the menu case solving the seat a second time), two
           attribute changes and a restyle on every solve for a seat that was
           then not the one measured. Both are decided first now, and the
           attribute is written only when it changes. */
        const exitInPanel = Boolean(returnControl?.closest?.('#audio-settings-panel'));
        const wantsExitBand = compactRail && !reseatingCommandLane
            && !usesPhoneCommandRail() && !exitInPanel;
        setDatasetValue(endTurn, 'returnLayout', wantsExitBand ? 'below' : null);
        seatEndTurnBetweenSummoners(endTurn);
        let etRect = endTurn.getBoundingClientRect();
        if (etRect.width === 0 || etRect.height === 0) return;

        let elRect = el.getBoundingClientRect();
        if (elRect.width === 0) return;

        const gap = vmin() * 1.5;

        /* DIRECTLY BELOW End Turn, centred on it, RIGIDLY.

           Owner-specified, third and final form: "it should be below the end
           turn buttn, close to it, centered with it horizontally." BESIDE was
           tried and is the board's own lane — End Turn is pinned to the summoner
           column, so anything to its left sits where summoned cards are. ABOVE
           was tried and did not fit. Below is the only arrangement that is both
           what was asked for and off the board.

           The seam between the summoner clusters has to hold the whole pair
           (End Turn + gap + trigger), which is what sizes the crest — see
           --phone-summoner-portrait-width.

           The owner asked for it ABOVE — "u can put the volume button above the
           end turn buttn, centered, aligned" — and that is unaffordable, which
           is a measurement rather than a preference. A stacked pair is 92px
           tall. At 844x288 the seam between the two summoner clusters needs the
           crest at W <= 75.5px to leave that much room, while the summoner
           status plate (bottom-anchored, growing upward, 50px for the canonical
           ledger) needs the crest at W >= 91.6px. Those do not intersect, and
           the visible symptom was End Turn stepping outboard to x587 — off the
           command rail, floating over the table — on the owner's own device box.

           Beside costs 0px of seam and restores both. The defect that prompted
           the request — "the summoned card slots are overlapping it" — is fixed
           at its actual cause instead: board cards are now in every obstacle
           set (battleBoardCardRects), so the lane this sits in is checked
           against the cards that were invisible to it before.

           This is also the root-cause fix for "the summoned card slots are
           overlapping it". The trigger used to be seated to End Turn's LEFT, and
           End Turn is pinned to the summoner column at the right edge — so that
           lane is the board's, and a summoned card renders larger than the slot
           it sits in. Stacking removes the trigger from the board's horizontal
           lane altogether.

           No search, no per-layout branch: the trigger is a satellite of End
           Turn and must move with it, or the pair drifts apart and the placement
           stops reading as one control group. Declared BEFORE measuring, because
           the phone CSS keys End Turn's own seat off this marker. */
        if (usesPhoneCommandRail()) {
            /* Both corner controls are CSS-owned on a phone. Hand the trigger
               back and never seat it here, or the inline seat outranks the
               corner anchor forever.

               THE EXIT MUST BE HANDED BACK IN THE SAME BREATH. This branch used
               to release only the volume trigger and return, which left
               `#skip-tutorial-btn` wearing whatever inline seat the DESKTOP
               solver last gave it — an inline `left`/`top` beats the corner
               anchor at any specificity, so a session that reached a phone page
               box by rotating or resizing rather than by loading at one parked
               the exit off-screen and kept it there. Measured after a
               1400x640 -> 844x288 resize: the X frozen at [1172,332], 328px
               outside a page box 844px wide, with the corner it is supposed to
               occupy left empty.

               `returnLayout` must go too: it is set earlier in this pass to
               reserve a band under End Turn for the exit, and on a phone the
               exit is in the corner — nothing sits under End Turn, so reserving
               that band just pushes the button off the summoner centre line. */
            delete endTurn.dataset.audioLayout;
            delete endTurn.dataset.returnLayout;
            releaseCorner(el);
            if (returnControl) {
                returnSeatPins.clear();
                returnControl.style.left = '';
                returnControl.style.top = '';
                returnControl.style.right = '';
                returnControl.style.bottom = '';
                returnControl.style.transform = '';
                delete returnControl.dataset.layout;
            }
            if (isOpen) positionPanel();
            return;
        }
        /* THE EXIT MAY HAVE LEFT THE RAIL ENTIRELY.

           `chrome-menu.js` folds Return-to-Lobby into the menu panel for every
           match that is not the guest tutorial, on the owner's instruction. A
           control inside a panel must not be seated by this solver: writing
           `position: fixed` coordinates onto it would tear it back out of the
           panel and park it over the board.

           When it has gone, End Turn also stops reserving the band beneath
           itself — `returnLayout` describes a pair that no longer exists — so
           it centres alone between the summoners. That IS a movement of End
           Turn, and it is the intended consequence of removing the control
           under it rather than drift; `chrome-menu-battle.real.spec.js`
           measures where it lands. */
        /* THE MENU SEATS ITSELF FIRST, before anything can return early.

           This used to sit below the exit branch, and the moment that branch
           started returning early for a panelled exit the clearance stopped
           running at all: the menu printed 12x12px over the enemy summoner's
           portrait at 1400x640. Its seat does not depend on the exit's, so it
           has no business being downstream of it. */
        releaseCorner(el);
        delete endTurn.dataset.audioLayout;
        /* NO BATTLE NUDGE. The menu keeps its lobby seat in a match.

           It used to lift clear of the enemy summoner's portrait, which moved
           it to `top: 0` at 1400x640 — and that is BOTH defects the owner
           reported in one: "its pos shouldnt change between lobby and game"
           and "still doesn't maintain perfect padding distance", because a
           button at top 0 with a 14px right inset is visibly not square in the
           corner. The lobby seat is the final seat; the trigger already paints
           above the portrait (z-index 10002), so the corner it shares is a
           stacking question, not a placement one. */
        elRect = el.getBoundingClientRect();

        if (exitInPanel) {
            returnControl.style.left = '';
            returnControl.style.top = '';
            returnControl.style.right = '';
            returnControl.style.bottom = '';
            returnControl.style.transform = '';
            delete returnControl.dataset.layout;
            returnSeatPins.clear();
            // End Turn was seated alone above: no band was reserved for an
            // exit that is not on the rail.
            if (isOpen) positionPanel();
            return;
        }

        const seatedRect = endTurn.getBoundingClientRect();
        if (seatedRect.width > 0 && seatedRect.height > 0) etRect = seatedRect;

        const safe = safeAreaInsets();

        /* THE MENU IS NOT A SATELLITE OF END TURN ANY MORE.

           It used to be seated here, beside End Turn on desktop and tablet and
           stacked under it on a phone, because it was the volume puck and the
           command rail was the only place a volume puck belonged. It is the
           whole chrome menu now, and chrome has one home: the top-right corner,
           on every surface and every device. Handing the element back to CSS is
           what puts it there — the same `--mf-rail-*` tokens the lobby uses, so
           there is exactly one seat to reason about instead of one per surface.

           This also retires `endTurn.dataset.audioLayout`. The attribute meant
           "the audio trigger is beside me, so I do not have to yield space for
           it"; with nothing beside End Turn to yield to, the only pairing left
           is the exit, which `returnLayout` already describes. Deleting it is
           the point of the change, not a side effect.

           This used to name `chromeMenuBattleSeat.test.js` as the measurement
           that made "nothing unrelated moved" a failing test rather than a
           claim. THAT FILE DOES NOT EXIST anywhere in the repository — a
           comment asserting coverage that nothing provides is worse than no
           comment, because the next reader stops looking. What actually
           measures this rail is
           `tests/e2e-playwright/real/chrome-menu-battle.real.spec.js` ("the
           battle menu clears every command-rail element at every page box"). */

        /* The lane bounds the exit solver clamps into. Unchanged expressions —
           they were declared inside the satellite block that is now gone, and
           the exit's placement must not shift because the menu moved. */
        const minLeft = safe.left + gap;
        const maxLeft = Math.max(
            minLeft,
            window.innerWidth - safe.right - elRect.width - gap,
        );

        /* Return to Lobby gets the same treatment Audio already had. Stacking it
           above End Turn is the preferred shape, but on a phone the band above
           End Turn belongs to the opponent's nameplate — measured at
           [756,67,88,24] against a Return control at [707,80,129,44] — so it
           needs the identical "blocked, go beside instead" escape. When Audio
           has ALSO been displaced beside End Turn, Return steps past it rather
           than landing on top of it. */
        /* THE EXIT IS THE THIRD MEMBER OF THE COMMAND RAIL, on compact rails.

           It has been seated by a SEARCH for this control's whole history, and
           every report about it is a symptom of that: "the lobby button is on
           the left side of screen ... it looks random", "it must not under any
           circustance sit undreneath the storyboard squares", "i just locked
           screen and lobby button still moved around". A searched seat lands
           somewhere different on every page box, so muscle memory cannot form.

           Every previous attempt at a declared home tried ABOVE the pair or
           BELOW it, and measurement killed both: the bands there are 3-21px and
           24-53px against a 44px control, so it fits on at most two of the five
           real page boxes. BESIDE was never tried. Measured across all five,
           laying the three controls out horizontally — exit, audio, End Turn, on
           one centre line — needs 198-203px against 207-311px available between
           the board's right edge and the safe inset. It fits everywhere, with
           22-110px to spare.

           So on a phone the exit becomes a rigid satellite like the trigger: no
           search, no per-box outcome, one place a player can learn. Desktop and
           tablet keep the search and its declared seat beneath End Turn — they
           have the room, and this rail was never measured against them. */
        if (returnControl && compactRail && usesPhoneCommandRail()) {
            /* CSS owns the phone exit now (top-right corner, mobile.css).
               Hand the element back and never seat it here. */
            returnControl.style.left = '';
            returnControl.style.top = '';
            returnControl.style.right = '';
            returnControl.style.bottom = '';
            returnControl.style.transform = '';
            delete returnControl.dataset.layout;
            delete endTurn.dataset.returnLayout;
            if (isOpen) positionPanel();
            return;
        }
        const returnRect = returnControl?.getBoundingClientRect?.();
        if (returnRect && returnRect.width > 0 && returnRect.height > 0) {
            /* ONE sweep, two predicates.

               The seat search probes a handful of candidates and the pin check
               runs first, so re-reading the DOM per candidate would put several
               full querySelectorAll passes inside a rAF on a phone. The declared
               lane disagrees with the search about the summoner CLUSTER and
               about nothing else, so everything else is measured once here and
               both read it. It used to be written out twice and swept twice, a
               hundred lines apart in the same rAF — and two lists that must
               agree are two lists that drift: the dev concede and the countdown
               were each added to one of them and not the other, and each
               omission cost a live overlap. */
            const sharedObstacles = battleSlotRects()
                .concat(battleBoardCardRects())
                .concat(battleContentRects())
                .concat(battleStatusRects())
                .concat(battleDebugControlRects())
                .concat(battleCountdownRects());
            const seatCtx = {
                obstacles: battleProfileRects().concat(sharedObstacles),
                /* THE MENU'S REAL BOX, straight from the measurement above.

                   This used to be synthesised from the `left`/`top` that the
                   satellite solve had just computed for the trigger. That solve
                   is gone, so those two names no longer existed — and because
                   this is live code, `reposition()` threw a ReferenceError here
                   on EVERY battle pass, silently, before it ever reached the
                   exit's seat search. The exit then kept a CSS fallback that
                   overlapped End Turn by 77x17px at 1400x640. Nothing logged it;
                   the A/B probe against main is what found it.

                   The menu is a real element with a real rect, so the honest
                   obstacle is simply that rect. It sits in the top-right corner
                   and will not normally intersect the command lane at all,
                   which is the point — the exit gets the seat it always wanted
                   instead of dodging a control that has left the rail. */
                audioRect: elRect,
                etRect,
                safe,
            };
            const seatKey = `${window.innerWidth}x${window.innerHeight}:`
                + `${safe.left},${safe.top},${safe.right},${safe.bottom}`;
            const pinned = returnSeatPins.get(seatKey);
            if (pinned) {
                const pinnedBox = {
                    left: pinned.left,
                    top: pinned.top,
                    width: returnRect.width,
                    height: returnRect.height,
                    right: pinned.left + returnRect.width,
                    bottom: pinned.top + returnRect.height,
                };
                /* VALIDATE THE PIN AGAINST EVERYTHING, not just the hands.
                   This used to ask only `handCardRects()`, so a pin survived
                   any other obstacle moving underneath it and the early return
                   below skipped the search entirely. The seat is keyed by
                   viewport size, so once pinned it was re-applied for the rest
                   of the match no matter what changed — which is why this
                   control kept ending up somewhere wrong and stayed there.
                   Measured at 844x280: pinned seat 142-186, story feed grown to
                   8-218 on the same column, and the pin was re-applied straight
                   underneath it. The seat may only be reused while it is still
                   clear of the SAME obstacle set the search uses. */
                if (returnSeatIsClear(pinnedBox, seatCtx)) {
                    /* A pin short-circuits the whole search, so the `below`
                       marker has to be reconciled HERE as well. Without this the
                       marker is re-declared at the top of every pass, the pin
                       returns before the correction further down can undo it,
                       and End Turn keeps yielding a band the pinned exit never
                       occupies — measured 125..244 against a 172 midpoint at
                       852x344 even with the later correction in place. */
                    const pinnedUnderEndTurn = pinned.left < etRect.right
                        && pinned.left + returnRect.width > etRect.left;
                    if (compactRail && !pinnedUnderEndTurn
                        && endTurn.dataset.returnLayout === 'below' && !reseatingCommandLane) {
                        delete endTurn.dataset.returnLayout;
                        reseatingCommandLane = true;
                        try { reposition(); } finally { reseatingCommandLane = false; }
                        return;
                    }
                    returnControl.style.left = `${pinned.left}px`;
                    returnControl.style.top = `${pinned.top}px`;
                    returnControl.style.right = 'auto';
                    returnControl.style.bottom = 'auto';
                    returnControl.style.transform = 'none';
                    returnControl.dataset.layout = pinned.layout;
                    if (isOpen) positionPanel();
                    return;
                }
                // Something claimed the seat. Re-solve and re-pin below.
                returnSeatPins.delete(seatKey);
            }
            const above = {
                left: etRect.left + etRect.width / 2 - returnRect.width / 2,
                top: etRect.top - gap - returnRect.height,
                width: returnRect.width,
                height: returnRect.height,
            };
            above.right = above.left + above.width;
            above.bottom = above.top + above.height;

            /* One predicate, shared with the pin check above. Two obstacle sets
               that disagree about what counts as an obstacle is how the next
               seat gets it wrong. */
            const fits = (candidateLeft, candidateTop) => {
                const box = {
                    left: candidateLeft,
                    top: candidateTop,
                    width: returnRect.width,
                    height: returnRect.height,
                    right: candidateLeft + returnRect.width,
                    bottom: candidateTop + returnRect.height,
                };
                return returnSeatIsClear(box, seatCtx);
            };

            // Preferred, then beside End Turn, then the free column outboard of
            // the leftmost slot — which is the only place on a short phone that
            // is neither HUD nor board.
            /* The column is defined by the BOARD's left edge only. Deriving it
               from every obstacle folded in the full-width hands, whose left is
               0, which collapsed the column back onto the narration lane — and
               the lane's height varies with its content, so the placement then
               differed between a match and the tutorial. */
            const slotLeft = battleSlotRects().reduce(
                (min, o) => Math.min(min, o.left),
                window.innerWidth,
            );
            const columnLeft = Math.max(minLeft, slotLeft - gap - returnRect.width);
            const besideTop = etRect.top + (etRect.height - returnRect.height) / 2;
            const columnTop = Math.max(
                safe.top,
                window.innerHeight - safe.bottom - gap - returnRect.height,
            );
            /* The LOW home comes first. Exiting is the rarest thing a player
               does in a battle, so the control belongs out of the way, low in
               the free column — not level with End Turn, which is where the
               beside-candidate kept putting it. The scan and the higher
               candidates stay underneath as fallbacks, and every one of them
               still goes through fits(), so preferring the low seat cannot
               introduce an overlap in a normal match or in the tutorial. */
            const besideCandidate = {
                left: Math.max(minLeft, Math.min(etRect.left - gap - returnRect.width, maxLeft)),
                top: besideTop,
            };
            const aboveCandidate = {
                left: Math.max(minLeft, Math.min(above.left, maxLeft)),
                top: above.top,
            };
            /* The rail's own seat: directly under End Turn, sharing its centre.
               It still goes through fitsInLane(), so a tutorial tip or a crowded
               page box falls through to the step-downs and then to the search
               below rather than producing an overlap. */
            const laneLeft = Math.max(
                minLeft,
                Math.min(etRect.left + etRect.width / 2 - returnRect.width / 2, maxLeft),
            );
            /* SHARING THE LANE IS NOT PAINTING ON THE PORTRAIT. Excluding the
               whole cluster let the seat under End Turn be accepted while it
               sat 364px² on top of the player's portrait — measured on the
               iPhone 13/14 page box, which the geometry oracle rightly refuses.
               The portraits therefore come back as obstacles; the NAMEPLATES do
               not, because they are the thin strip the lane is aligned against
               and treating them as blockers is what re-homed the exit into the
               far column in the first place. When the low seat is taken the
               control still has the next seat DOWN the same lane — the answer
               the 960x600 status-ribbon case uses — so alignment survives and
               the overlap does not. */
            /* The declared lane is tried FIRST, so anything it cannot see is
               something the search below never gets a chance to dodge. That is
               how the dev concede kept being covered even after it became a
               seat-search obstacle: the declared seat under End Turn is exactly
               where its CSS pins it, fitsInLane said "clear", and the search
               never ran. Both predicates now read one measured list. */
            const laneObstacles = sharedObstacles
                .concat(['player-portrait', 'opponent-portrait']
                    .map((id) => document.getElementById(id)?.getBoundingClientRect?.())
                    .filter((r) => r && r.width > 0 && r.height > 0));
            /* THE LANE RUNS DOWNWARD, NEVER UP.

               This was "under End Turn first, then ABOVE it", because a
               summoner's status ribbon can reach into the band below End Turn on
               a tablet page box (measured at 960x600: the exit clipped the
               player's ribbon by 9px) and the other seat in the same lane looked
               like the answer. It is not a seat at all — the band above End Turn
               belongs to the COUNTDOWN, at every viewport. A chip hangs its
               bottom edge at `endTurnCentre - 4.6vmin` while End Turn measures
               7.2-8.0vmin tall across the whole ladder, so the chip reaches
               0.6-1.0vmin above the button's top edge — and this seat started
               1.5vmin above that same edge, i.e. inside it. There is no size at
               which the two miss each other, which is exactly what shipped:
               "skip tutorial button overlaps mulligan timer on my desktop
               monitor", measured at 6037px² (1920x1080) through 19137px² (4K).

               So the lane steps DOWN. Whatever holds the band directly under End
               Turn — the dev concede is CSS-pinned there, a status ribbon can
               reach it — is stepped past on End Turn's own centre line, and the
               exit leaves the lane only when the lane is genuinely full. Every
               seat still goes through fitsInLane(). */
            const laneStepDowns = [...new Set(laneObstacles
                .filter((o) => o.left < laneLeft + returnRect.width
                    && o.right > laneLeft
                    && o.bottom > etRect.bottom
                    && o.bottom + gap + returnRect.height
                        <= window.innerHeight - safe.bottom)
                .map((o) => Math.round(o.bottom)))]
                .sort((a, b) => a - b)
                /* Bounded: a crowded lane must not turn a fixed candidate list
                   into an unbounded one inside a rAF. */
                .slice(0, 4)
                .map((bottom) => ({ left: laneLeft, top: bottom + gap }));
            const candidates = compactRail
                ? [{ left: laneLeft, top: etRect.bottom + gap }, ...laneStepDowns]
                : [aboveCandidate, besideCandidate];
            /* Then a FIXED home in the free column: vertically centred in the
               band between the narration lane and the player's hand, both of
               which exist in every battle. A pure scan found the first free
               spot, which moved by 60px between a normal match and the tutorial
               because their obstacle sets differ — and "the exit button is
               somewhere else in the tutorial" was the original complaint. The
               scan stays underneath as a fallback, so a layout that fills the
               home still gets a valid placement rather than a collision. */
            const handTop = (document.getElementById('player-hand')?.getBoundingClientRect?.()?.top)
                || (window.innerHeight - safe.bottom);
            // Anchored to the hand, which is a fixed edge of the layout — not to
            // the narration lane, whose height follows its content.
            const homeTop = Math.round(handTop - gap - returnRect.height);
            /* PHONE PAGE BOXES GET A LEFT-RAIL HOME, AND IT COMES FIRST. The
               portrait shelf made the summoner column a constant size, and the
               band it leaves under End Turn (32px at 932x300, measured) can
               never seat a 44px control — so on a phone the lane seats always
               fail and the exit always re-homes. The owner accepted that side
               ("i can accept a move to that side. but its placement is too
               random") — what read as random was the free-column x, which is
               derived from the slot grid and visually anchored to nothing.
               Align the exit's left edge with the story feed's instead: the
               two form one left-hand chrome rail, bottom-anchored above the
               hand, as low as the hand's own obstacle strip allows. */
            const phoneBox = window.innerHeight <= 560;
            if (phoneBox) {
                const feedRect = document.getElementById('story-feed')?.getBoundingClientRect?.();
                const railLeft = (feedRect && feedRect.width > 0)
                    ? Math.max(minLeft, feedRect.left)
                    : Math.max(minLeft, safe.left + gap);
                candidates.unshift({ left: railLeft, top: homeTop });
            }
            candidates.push({ left: columnLeft, top: homeTop });
            // Only if the compact seat is occupied does it climb back up.
            if (compactRail) candidates.push(besideCandidate, aboveCandidate);

            const scanBottom = window.innerHeight - safe.bottom - returnRect.height;
            if (phoneBox) {
                // Fallbacks scan LOW-FIRST on a phone: a first-fit from the top
                // parked the exit high in the column, which is the other half
                // of "placement is too random". Low is where an exit belongs.
                for (let top = scanBottom; top >= safe.top + gap; top -= 6) {
                    candidates.push({ left: columnLeft, top });
                }
            } else {
                for (let top = safe.top + gap; top <= scanBottom; top += uiPx(6)) {
                    candidates.push({ left: columnLeft, top });
                }
            }
            /* The fallback has to stay REACHABLE even when nothing fits. It
               used to be a raw {columnLeft, columnTop}, which does not go
               through fits() — so on a crowded phone layout the exit landed
               below the safe line, inside the home-indicator strip, where iOS
               eats the tap. That is "Return to Lobby button doesn't work": the
               button was there, visible, correctly sized, and the system was
               taking the touch. Overlapping some art is recoverable; being
               untappable is not, so the fallback is clamped into the safe box. */
            /* The exit's OWN width, not the audio control's — `maxLeft` above is
               derived from the popover's box, so reusing it left a wider exit
               half a pixel off-screen on a 667-wide page box.

               Bounded by the same inset the lane itself uses. End Turn and the
               summoner portraits sit at env(safe-area-inset-right); clamping
               the exit to the raw viewport edge let it slide out from under the
               button it is aligned to and into the notch, where the system eats
               the tap. */
            const returnMaxLeft = Math.max(
                minLeft,
                window.innerWidth - safe.right - returnRect.width,
            );
            const clampIntoSafeArea = (candidate) => ({
                left: Math.max(minLeft, Math.min(candidate.left, returnMaxLeft)),
                top: Math.max(
                    safe.top,
                    Math.min(
                        candidate.top,
                        window.innerHeight - safe.bottom - returnRect.height,
                    ),
                ),
            });
            /* The summoner cluster is NOT an obstacle for the seat under End
               Turn — it is the thing that seat is aligned to. Both controls
               take the summoner lane by construction (right:0 at
               --phone-command-banner-width on a phone; the same column on
               desktop), so counting the portraits and nameplates as collisions
               made the declared seat fail its own test and re-homed the exit
               into the free column off to the left. Reported as "End Turn and
               Return are not aligned with the profile pictures".

               Everything else still counts: the slots and the hand are
               interactive and must never be covered, and the safe-area clamp
               below still applies, because untappable is the one outcome that
               is never recoverable. */
            const fitsInLane = (left, top) => {
                const box = { left, top, width: returnRect.width, height: returnRect.height };
                box.right = left + returnRect.width;
                box.bottom = top + returnRect.height;
                return !laneObstacles.some((o) => rectsOverlap(box, o))
                    && !rectsOverlap(box, seatCtx.audioRect)
                    && !rectsOverlap(box, etRect)
                    && top >= safe.top
                    && box.bottom <= window.innerHeight - safe.bottom;
            };
            /* Clamped even when it fits: End Turn sits flush to the lane's right
               edge, so centring the exit on it can put its last half-pixel past
               the viewport (measured 667.5 on a 667-wide box). Off-screen by any
               amount is the one outcome that is never recoverable. */
            const declaredLane = compactRail ? candidates : candidates.slice(0, 1);
            const declared = declaredLane.find((c) => fitsInLane(c.left, c.top));
            const chosen = declared
                ? clampIntoSafeArea(declared)
                : candidates.find((c) => fits(c.left, c.top))
                    || clampIntoSafeArea({ left: columnLeft, top: columnTop });
            const returnLeft = chosen.left;
            const returnTop = chosen.top;
            const blockedAbove = chosen !== candidates[0];

            /* Round INWARD at the edges. A control 130.5px wide seated at 536.5
               rounds to 537 and then measures 667.5 on a 667-wide page box —
               half a pixel off-screen, which the geometry oracle rightly
               refuses. Rounding is the last thing that happens to a position,
               so it is the last place the bound has to hold. */
            const maxRoundedLeft = Math.floor(window.innerWidth - returnRect.width);
            const maxRoundedTop = Math.floor(window.innerHeight - returnRect.height);
            const seatLeft = Math.min(Math.round(returnLeft), maxRoundedLeft);
            const seatTop = Math.min(Math.round(returnTop), maxRoundedTop);
            returnControl.style.left = `${seatLeft}px`;
            returnControl.style.top = `${seatTop}px`;
            returnControl.style.right = 'auto';
            returnControl.style.bottom = 'auto';
            returnControl.style.transform = 'none';
            returnControl.dataset.layout = blockedAbove ? 'end-turn-beside' : 'end-turn-above';

            /* The `below` pre-shift is only honest if the exit ACTUALLY landed
               under End Turn. The marker is declared before measuring (it has
               to be — the CSS seat depends on it), but the exit often cannot
               fit that band and falls through to the free far column. End Turn
               then goes on yielding half a band for a control that is nowhere
               near it, and the command lane sits high by (44px + 1.5vmin)/2.

               Measured on clean main, WebKit at 852x344: End Turn 125.4-169.4
               at x 748-852 while the exit sat at 200-244, x 5-108 — a "group"
               midpoint of 184.5 against a viewport midpoint of 172. That 12.5px
               is the pre-existing "command lane centers as one painted group"
               failure in Firefox AND WebKit, which Chromium happened to pass.

               One bounded correction: drop the marker and re-run, guarded so a
               layout that keeps changing its mind cannot oscillate. */
            const landedUnderEndTurn = seatLeft < etRect.right
                && seatLeft + returnRect.width > etRect.left;
            if (compactRail && !landedUnderEndTurn
                && endTurn.dataset.returnLayout === 'below' && !reseatingCommandLane) {
                delete endTurn.dataset.returnLayout;
                reseatingCommandLane = true;
                try { reposition(); } finally { reseatingCommandLane = false; }
                return;
            }

            if (!returnSeatPins.has(seatKey)) {
                if (returnSeatPins.size >= RETURN_SEAT_PIN_CAP) {
                    returnSeatPins.delete(returnSeatPins.keys().next().value);
                }
                returnSeatPins.set(seatKey, {
                    left: seatLeft,
                    top: seatTop,
                    layout: returnControl.dataset.layout,
                });
            }
        }
        if (isOpen) positionPanel();
    }

    let repositionRaf = 0;
    function scheduleReposition() {
        if (repositionRaf) return;
        const raf = (typeof window !== 'undefined' && window.requestAnimationFrame)
            ? window.requestAnimationFrame
            : (cb) => setTimeout(cb, 16);
        repositionRaf = raf(() => {
            repositionRaf = 0;
            // A frame scheduled just before navigation can fire mid-teardown.
            try { reposition(); } catch (_) { /* unloading */ }
        });
    }

    let watchersInstalled = false;
    let resizeObserver = null;
    const observed = new WeakSet();
    let handsObserver = null;
    const observedHands = new WeakSet();

    /* Cards entering or leaving a hand move the obstacle set (deal, draw,
       play), and nothing else re-runs the positioner then: the body observer
       only sees BODY's own children, and no observed element resizes. Watch
       the hand containers' child lists; the rAF gate in scheduleReposition
       coalesces a whole deal into one pass, measured a frame after the
       insertion. */
    function ensureHandsObserved() {
        if (typeof MutationObserver !== 'function') return;
        if (!handsObserver) {
            handsObserver = new MutationObserver(() => {
                // Records can be delivered while the page is tearing down;
                // touching the document then throws.
                try { scheduleReposition(); } catch (_) { /* unloading */ }
            });
        }
        for (const id of ['player-hand', 'opponent-hand']) {
            const el = document.getElementById(id);
            if (el && !observedHands.has(el)) {
                handsObserver.observe(el, { childList: true });
                observedHands.add(el);
            }
        }

        /* The SUMMONER CLUSTERS arrive late and change shape.
           A nameplate is built once the summoner's name is known, and a name
           that balances onto two lines makes the cluster taller than the one
           the positioner measured. Measured live at 844x288: End Turn was
           seated while the opponent had no plate, "Sylva Thornheart" then
           rendered at 75-99, and nothing re-ran — so the button sat at 79-123,
           straight through it. Watch the containers so a late or re-fitted
           plate re-seats the rail. */
        /* WATCH THE PORTRAIT, NOT A WRAPPER THAT DOES NOT EXIST. This loop
           used to ask for `player-portrait-container` / `-container`. Nothing
           creates those ids -- indicators.js builds `#player-portrait` and
           `#opponent-portrait` and appends the nameplate to THOSE, and only
           indicators.css still names the wrapper form. Both getElementById
           calls returned null, so nothing was ever observed and the fix above
           never ran once. `.portrait-container` stays supported because the
           codebase treats it as an optional alternative mount. */
        const clusters = new Set();
        for (const id of ['player-portrait', 'opponent-portrait']) {
            const portrait = document.getElementById(id);
            if (portrait) clusters.add(portrait.closest('.portrait-container') || portrait);
        }
        for (const el of clusters) {
            if (observedHands.has(el)) continue;
            handsObserver.observe(el, { childList: true, subtree: true, characterData: true });
            observedHands.add(el);
        }
    }
    function ensureObserved() {
        if (!resizeObserver) return;
        for (const id of [
            'end-turn-btn',
            'skip-tutorial-btn',
            ROOT_ID,
            'player-portrait',
            'opponent-portrait',
            'player-health-indicator',
            'player-mana-indicator',
            'opponent-health-indicator',
            'opponent-mana-indicator',
            /* The NARRATION LANE grows. It was already an obstacle in
               battleContentRects(), but nothing watched it, so a seat chosen
               against a short feed went stale the moment a story tile or manga
               panel landed — and the feed paints at z-index 9535 against the
               Return control's 9450, so it covers rather than reflows. Measured
               at 844x280: feed 8-122, "Go to Lobby" 142-186; one panel of
               growth swallows the button's leading edge, which is the reported
               half-covered "to Lobby". Listing an obstacle without observing it
               is a stale seat waiting to happen. */
            'story-feed',
            /* THE SUMMONER STATUS RIBBONS, for the identical reason. They
               joined battleStatusRects() when the volume trigger moved above
               End Turn — the trigger now claims the band the ribbon occupies —
               but an obstacle that is measured and NOT observed is a stale seat
               waiting to happen, which is exactly what the comment above this
               one says about the narration lane.

               A ribbon grows as effects are injected during a turn. The pair was
               seated against the short version and then the ribbon grew into it:
               measured trigger [496,49]-[540,93] against a ribbon that had
               reached [522.45,23.25]-[650.45,50.25], a 17.5 x 1.25px overlap.
               A 1.25px miss is what an un-observed obstacle looks like. */
            'player-status-indicator',
            'opponent-status-indicator',
        ]) {
            const el = document.getElementById(id);
            if (el && !observed.has(el)) {
                resizeObserver.observe(el);
                observed.add(el);
            }
        }
    }
    function installWatchers() {
        if (watchersInstalled) return;
        watchersInstalled = true;

        window.addEventListener('resize', scheduleReposition, { passive: true });

        if (typeof MutationObserver === 'function') {
            const bodyObs = new MutationObserver(() => {
                // Same teardown guard as the hands observer below.
                try {
                    ensureObserved();
                    ensureHandsObserved();
                    scheduleReposition();
                } catch (_) { /* unloading */ }
            });
            bodyObs.observe(document.body, {
                attributes: true,
                attributeFilter: ['class'],
                childList: true,
            });
        }
        if (typeof ResizeObserver === 'function') {
            resizeObserver = new ResizeObserver(scheduleReposition);
            ensureObserved();
        }
        ensureHandsObserved();
        /* `moonfall:i18n-translated` only. The sibling line here used to be
           `moonfall:language-changed`, which is a typo for the real
           `moonfall:languagechange` AND on the wrong target — i18n dispatches
           that one on `window`. It never fired. applyTranslations() runs on
           every setLanguage, so this one signal covers both moments. */
        document.addEventListener('moonfall:i18n-translated', scheduleReposition);
        document.addEventListener('moonfall:status-layout-changed', () => {
            scheduleReposition();
        });
    }

    document.addEventListener('DOMContentLoaded', build);
    build();

    return {
        build,
        refresh,
        reposition,
        open,
        close: () => close(true),
        toggle: toggleOpen,
        isOpen: () => isOpen,
    };
})();

export { AudioSettings };
